// @ts-strict-ignore
import React from 'react'
import { startCase, toLower } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { SidePanelSummary } from 'components/SidePanel/SidePanelSummary'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { Application, CompanyDetails, CustomerApplicationDetails } from 'types/customers'
import { statusLabelMap } from '../utils/utils'

interface ApplicationSidePanelBodyProps {
  application: Application | undefined
  data: CustomerApplicationDetails | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

interface ApplicationDetailsSectionProps {
  reference: string | undefined
  id: string | undefined
  type: string | undefined
  model: string | undefined
  status: Application['status'] | undefined
}

interface CompanyDetailsSectionProps {
  company: CompanyDetails
}

interface BusinessProfileSectionProps {
  company: CompanyDetails
}
const formatEnumValue = (value: string) => startCase(toLower(value))

const ApplicationDetailsSection = ({
  reference,
  id,
  type,
  model,
  status,
}: ApplicationDetailsSectionProps) => {
  const { t } = useTranslation()
  return (
    <SidePanelSection heading={t('customers.applicationDetails')}>
      <SidePanelSummary
        rows={[
          {
            label: t('customers.externalReference'),
            content: ellipseMiddleText(reference || id || '', 12, 8),
            ...((reference || id) && { copyText: reference || id }),
          },
          { label: t('customers.customerType'), content: type || '' },
          { label: t('customers.onboardingType'), content: model || '' },
          { label: t('status'), content: (status && statusLabelMap[status]) || status || '' },
        ]}
      />
    </SidePanelSection>
  )
}



const CompanyDetailsSection = ({ company }: CompanyDetailsSectionProps) => {
  const { t } = useTranslation()
  return (
    <SidePanelSection heading={t('customers.companyDetails')}>
      <SidePanelSummary
        rows={[
          ...(company.name ? [{ label: t('customers.businessName'), content: company.name }] : []),
          ...(company.entityType
            ? [
                {
                  label: t('customers.entityType'),
                  content: formatEnumValue(company.entityType),
                },
              ]
            : []),
          ...(company.registrationNumber
            ? [
                {
                  label: t('customers.registrationNumber'),
                  content: company.registrationNumber,
                },
              ]
            : []),
          ...(company.taxIdentification?.number
            ? [
                {
                  label: t('customers.taxNumber'),
                  content: company.taxIdentification.number,
                },
              ]
            : []),
          ...(company.taxIdentification?.taxResidenceCountryCode
            ? [
                {
                  label: t('customers.taxResidency'),
                  content: company.taxIdentification.taxResidenceCountryCode,
                },
              ]
            : []),
        ]}
      />
    </SidePanelSection>
  )
}

const BusinessProfileSection = ({ company }: BusinessProfileSectionProps) => {
  const { t } = useTranslation()
  const { businessProfile } = company
  return (
    <SidePanelSection heading={t('customers.businessProfile')}>
      <SidePanelSummary
        rows={[
          ...(businessProfile.naicsCode
            ? [{ label: t('customers.naicsCode'), content: businessProfile.naicsCode }]
            : []),
          ...(businessProfile.monthlyExpectedVolumes
            ? [
                {
                  label: t('customers.monthlyExpectedVolumes'),
                  content: formatEnumValue(businessProfile.monthlyExpectedVolumes),
                },
              ]
            : []),
          ...(businessProfile.sourceOfFunds
            ? [
                {
                  label: t('customers.sourceOfFunds'),
                  content: formatEnumValue(businessProfile.sourceOfFunds),
                },
              ]
            : []),
          ...(businessProfile.intendedUseOfAccount
            ? [
                {
                  label: t('customers.intendedUse'),
                  content: formatEnumValue(businessProfile.intendedUseOfAccount),
                },
              ]
            : []),
        ]}
      />
    </SidePanelSection>
  )
}

export const CustomerSidePanelBody = ({
  application,
  data,
  isLoading,
  isError,
  refetch,
}: ApplicationSidePanelBodyProps) => {
  if (isLoading) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (isError) {
    return (
      <Box paddingX={16} paddingY={32}>
        <RequestErrorStatusCard onReload={refetch} />
      </Box>
    )
  }

  const { reference, id, status, type, model } = application || {}

  const company = data?.company
  const hasCompanyDetails = !!company
  const hasBusinessProfile = !!company?.businessProfile

  return (
    <Box flexCol gapY={16} padding={16}>
      <ApplicationDetailsSection
        reference={reference}
        id={id}
        type={type}
        model={model}
        status={status}
      />
      {hasCompanyDetails && <CompanyDetailsSection company={company} />}
      {hasBusinessProfile && <BusinessProfileSection company={company} />}
    </Box>
  )
}
